import { useState, useEffect, useCallback } from 'react';
import { usePublicClient } from 'wagmi';
import type { Hex } from 'viem';
import { useNotify } from '@/feature/notifications';
import { getRevertReasonFromTx, RevertDecodingParams } from '../utils/decodeScError';
import { handleDecodedError, ReasonHandler } from './useErrorHandler';
import useAppStore from '../store/rootStore';
import { captureError } from '../utils/sentryHelpers';
import { CONTRACT_AWAIT_THRESHOLD, ONCHAIN_TX_RECEIPT_TIMEOUT_MS } from '@/common/timeout_constants';

export type TransactionStatus =
  | 'idle' // No transaction in progress
  | 'preparing' // Validating inputs, building transaction
  | 'switching_chain' // Switching network
  | 'signing' // Waiting for user to sign transaction
  | 'submitting' // Transaction submitted to mempool
  | 'confirming' // Waiting for blockchain confirmation
  | 'confirmed' // Transaction confirmed successfully
  | 'failed' // Transaction failed or was reverted
  | 'rejected'; // User rejected transaction or other error

interface TransactionMessages<TData> {
  confirming?: string;
  confirmed?: string | ((data?: TData) => string);
  failed: string;
  error: string;
  rejected: string;
}

interface UseTransactionStateProps<TData> {
  isWritePending: boolean;
  isWriteSuccess: boolean;
  isWriteError: boolean;
  txHash: Hex | undefined;
  processData?: TData;
  messages: TransactionMessages<TData>;
  onSuccess?: (data?: TData) => void;
  revertDecoding?: RevertDecodingParams;
  reasonHandler?: ReasonHandler<any>;
}

export function useTransactionState<TData>({
  isWritePending,
  isWriteSuccess,
  isWriteError,
  txHash,
  processData,
  messages,
  onSuccess,
  revertDecoding,
  reasonHandler,
}: UseTransactionStateProps<TData>) {
  const configChainId = useAppStore((state) => state.config?.chain.id);
  const publicClient = usePublicClient({ chainId: configChainId });
  const notify = useNotify();
  const [currentStatus, setCurrentStatus] = useState<TransactionStatus>('idle');

  useEffect(() => {
    if (isWriteError && (currentStatus === 'submitting' || currentStatus === 'confirming')) {
      setCurrentStatus('rejected');
      const debugInfo = txHash ? `Tx: ${txHash}` : undefined;
      notify(messages.rejected, 'warning', { debugInfo });
    }
  }, [isWriteError, currentStatus, notify, messages.rejected, txHash]);

  const handleTransactionFailure = useCallback(
    async (receipt: { blockNumber: bigint }) => {
      setCurrentStatus('failed');
      const debugInfo = txHash ? `Tx: ${txHash}` : undefined;

      if (revertDecoding) {
        try {
          const decoded = await getRevertReasonFromTx(
            publicClient!,
            txHash!,
            receipt.blockNumber,
            revertDecoding.to,
            revertDecoding.data,
            revertDecoding.from
          );
          handleDecodedError(decoded, messages.failed, notify, reasonHandler, debugInfo);
        } catch (error) {
          console.error('Failed to decode revert reason:', error);
          captureError(error, 'revert_reason_decoding', { txHash });
          notify(messages.failed, 'error', { debugInfo });
        }
      } else {
        notify(messages.failed, 'error', { debugInfo });
      }
    },
    [publicClient, txHash, revertDecoding, messages.failed, notify, reasonHandler]
  );

  const handleTransactionSuccess = useCallback(() => {
    setCurrentStatus('confirmed');
    onSuccess?.(processData);
    const confirmedMessage = typeof messages.confirmed === 'function' ? messages.confirmed(processData) : messages.confirmed;
    if (confirmedMessage) {
      // Strip 0x prefix and lowercase so this matches the API's order.c.txid / position.at.txid format
      const normalizedTxId = txHash?.replace(/^0x/i, '').toLowerCase();
      notify(confirmedMessage, 'success', { replaceKey: normalizedTxId ? `tx:${normalizedTxId}` : undefined });
    }
  }, [onSuccess, processData, messages.confirmed, notify, txHash]);

  const monitorTransaction = useCallback(async () => {
    if (!publicClient || !txHash) return;

    setCurrentStatus('confirming');

    let notificationTimeout;

    try {
      notificationTimeout = setTimeout(() => {
        if (messages.confirming) {
          notify(messages.confirming, 'info', { waiting: true });
        }
      }, CONTRACT_AWAIT_THRESHOLD);

      const receipt = await publicClient.waitForTransactionReceipt({
        hash: txHash,
        timeout: ONCHAIN_TX_RECEIPT_TIMEOUT_MS,
      });

      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
      }

      if (receipt.status === 'success') {
        handleTransactionSuccess();
      } else {
        await handleTransactionFailure(receipt);
      }
    } catch (error) {
      if (notificationTimeout) {
        clearTimeout(notificationTimeout);
      }
      setCurrentStatus('failed');
      const debugInfo = txHash ? `Tx: ${txHash}` : undefined;
      notify(messages.error, 'warning', { debugInfo });
      captureError(error, 'transaction_monitoring', { txHash });
      console.error('Failed to monitor transaction:', error);
    }
  }, [publicClient, txHash, messages.confirming, messages.error, notify, handleTransactionSuccess, handleTransactionFailure]);

  useEffect(() => {
    if (isWriteSuccess && txHash && currentStatus === 'submitting') {
      monitorTransaction();
    }
  }, [isWriteSuccess, txHash, currentStatus, monitorTransaction]);

  return {
    currentStatus,
    setCurrentStatus,
    isPending: isWritePending || ['confirming', 'preparing', 'switching_chain'].includes(currentStatus),
    isSuccess: currentStatus === 'confirmed',
    isError: currentStatus === 'failed' || currentStatus === 'rejected',
  };
}
